// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button } from '@mui/material';
import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';
import { IconButton, SectionContainer } from '@shared/ui-kit/components';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploader } from 'react-drag-drop-files';
import { useUploaderExcel } from '../hooks';

export interface UploaderExcelProps {
  typeAsset: string;
  setExcelData: React.Dispatch<React.SetStateAction<[] | PortfolioLoaded[]>>;
}

export const UploaderExcel: FC<UploaderExcelProps> = ({
  typeAsset,
  setExcelData,
}) => {
  const { t } = useTranslation();
  const { handleFileChange, setExcelFileError, excelFileError } = useUploaderExcel({
    setExcelData,
  });

  return (
    <SectionContainer
      title={t('portfolio.upload-excel-file', { ns: 'components' })}
      variant="h3"
      headerClass="justify-center"
      className="sticky top-4 text-center"
    >
      <div
        className="mt-4 grid rounded-lg border border-dashed border-primary"
        data-testid="btn-submit"
      >
        <FileUploader
          handleChange={handleFileChange}
          name="file"
          types={['XLSX']}
          onTypeError={setExcelFileError}
        >
          <div className="flex flex-col justify-center gap-2 p-4">
            <p className="font-semibold text-primary">
              {t('portfolio.drag-drop-file', { ns: 'components' })} <br />
            </p>
            <span className="font-normal text-primary-medium">
              {t('portfolio.limit-file-size', { ns: 'components' })}
            </span>
            <Button
              variant="tonal"
              color="secondary"
              data-testid="upInput"
              className="self-center !px-4 !py-2"
            >
              {t('portfolio.browse-file', { ns: 'components' })}
            </Button>
          </div>
        </FileUploader>
      </div>

      <a
        className="mt-4 flex items-center justify-center space-x-2 text-secondary hover:text-secondary/75"
        href={`/${typeAsset || 'stock'}_template.xlsx`}
        data-testid="download-link"
        download
      >
        <p className="hover:underline hover:underline-offset-4">
          {t('portfolio.download-template', { ns: 'components' })}
        </p>
        <IconButton
          icon="Download"
          variant="fill"
          className="!bg-secondary hover:!bg-secondary/75"
        />
      </a>

      {!!excelFileError && (
        <p
          className="font-semibold text-error-light"
          data-testid="excel-file-error"
        >
          {excelFileError}
        </p>
      )}
    </SectionContainer>
  );
};
